import PropTypes from 'prop-types';
import React from 'react';
import AccessibleIcon from '@material-ui/icons/Accessible';
import PedalBikeIcon from 'common/components/icons/PedalBikeIcon';
import moment from 'moment-timezone';
import _ from 'underscore';

import {
  isNextPickup,
  isNextDropoff,
  pickupEstimate,
  dropoffEstimate,
  rideStatus,
} from 'common/types/Ride';
import RidePayment from 'common/components/RidePayment';
import CapacityIcon from 'common/components/icons/CapacityIcon';
import MarkerAIcon from 'common/components/icons/MarkerAIcon';
import MarkerBIcon from 'common/components/icons/MarkerBIcon';
import TimeIcon from 'common/components/icons/TimeIcon';
import {serviceTypes} from 'common/types/Service';
import styles from './RideDetailsInput.css';


const accessibleStyle = {height: 22, width: 22, margin: '4px', verticalAlign: 'middle'};

const iconStyle = {height: 18, width: 'auto', margin: '4px', verticalAlign: 'middle', zoom: 0.9};

const rideStatusEvent = (status, ride) =>
  _.find(ride.statusHistory, (event) => event.status === status);

const ridePickupEvent = (ride) => rideStatusEvent(rideStatus.IN_PROGRESS, ride);

const rideDropoffEvent = (ride) => rideStatusEvent(rideStatus.COMPLETE, ride);

// eslint treats functions that return JSX as components, so the following functions disable that
// rule. Consider breaking out these functions into actual components.
const timeDetails = (
  // eslint-disable-next-line react/prop-types
  {load, ride, thresholds, timezone, onPickupDateTimeEdit}
) => {
  const pickupDateTime = ride.pickupDateTime || (ride.pickup ? ride.pickup.time : null);

  if (!ride.status) {
    // This is the confirmation page.
    let timeDisplay;
    if (pickupDateTime) {
      timeDisplay = (
        <span>
          <TimeIcon style={iconStyle} />
          {moment(pickupDateTime).tz(timezone).format('MMM D, YYYY, h:mma')}
        </span>
      );
    } else {
      timeDisplay = 'Next available';
    }

    return (
      <div className={styles.sectionItem}>
        {onPickupDateTimeEdit &&
          <div className={styles.edit} data-id="edit-datetime" onClick={onPickupDateTimeEdit}>
            Edit
          </div>
        }
        <div className={styles.heading}>Scheduled Pickup</div>
        <div className={styles.time} data-id="pickup-time-details">{timeDisplay}</div>
      </div>
    );
  } else if (
      [rideStatus.DENIED, rideStatus.CANCELED, rideStatus.NO_SHOW].indexOf(ride.status) >= 0
  ) {
    return (
      <div className={styles.sectionItem}>
        <div className={styles.heading}>Date & Time</div>
        <div className={styles.time} data-id="pickup-time-details">
          <TimeIcon style={iconStyle} />
          {moment(ride.createdAt).tz(timezone).format('M/DD/YYYY, h:mma')}
        </div>
      </div>
    );
  } else if (ride.status === rideStatus.COMPLETE) {
    return (
      <div className={styles.sectionItem}>
        <div className={styles.heading}>Date</div>
        <div className={styles.time} data-id="pickup-time-details">
          <TimeIcon style={iconStyle} />
          {moment(ridePickupEvent(ride).timestamp).tz(timezone).format('M/DD/YYYY')}
        </div>
      </div>
    );
  } else if (ride.status === rideStatus.IN_PROGRESS && isNextDropoff(ride)) {
    return (
      <div className={styles.sectionItem}>
        <div className={styles.heading}>Estimated drop-off</div>
        <div className={styles.time} data-id="pickup-time-details">
          <TimeIcon style={iconStyle} />
          {moment(dropoffEstimate(ride)).fromNow(true)}
        </div>
      </div>
    );
  } else if (ride.status === rideStatus.PENDING && isNextPickup(ride)) {
    return (
       <div className={styles.sectionItem}>
        <div className={styles.heading}>
          Estimated pickup
        </div>
        <div className={styles.time} data-id="pickup-time-details">
          <TimeIcon style={iconStyle} />
          {moment(pickupEstimate(ride)).fromNow(true)}
        </div>
      </div>
    );
  } else if (_.all([
    // service is in-advance or booked for a time in the future
    pickupDateTime,
    ride.serviceType === serviceTypes.INADVANCE || moment(pickupDateTime).isAfter(moment()),
  ])) {
    return (
      <div className={styles.sectionItem}>
        <div className={styles.heading}>Scheduled Pickup</div>
        <div className={styles.time} data-id="pickup-time-details">
          <TimeIcon style={iconStyle} />
          {moment(pickupDateTime).tz(timezone).format('MMM D, YYYY, h:mma')}
        </div>
      </div>
    );
  } else if (ride.status === rideStatus.PENDING) {
    const loadMessages = {
      high: `>${thresholds.high} minutes`,
      medium: `${thresholds.medium}–${thresholds.high} minutes`,
      low: `<${thresholds.medium} minutes`,
      unknown: 'Unknown',
    };
    const loadMessage = loadMessages[load];
    return (
      <div className={styles.sectionItem}>
        <div className={styles.heading}>
          Average Wait Time
        </div>
        <div className={styles.time} data-id="pickup-time-details">
          <TimeIcon style={iconStyle} />
          {loadMessage}
        </div>
      </div>
    );
  }
  return null;
};

const passengerDetails = (
  {capacity, wheelchair, bicycle, onPassengerDetailsEdit} // eslint-disable-line react/prop-types
) => (
  <div className={styles.sectionItem}>
    {onPassengerDetailsEdit &&
      <div className={styles.edit} data-id="edit-capacity" onClick={onPassengerDetailsEdit}>
        Edit
      </div>}
    <div className={styles.heading}>Passengers</div>
    <div className={styles.capacity}>
      <CapacityIcon style={iconStyle} />
      <span data-id="capacity-details">{capacity}</span>
    </div>
    <div>
    {wheelchair &&
      <span className={styles.wheelchair}>
        <AccessibleIcon style={accessibleStyle} htmlColor="#4A90E2" />
      </span>}
    {bicycle &&
      <span className={styles.bicycle}>
        <PedalBikeIcon style={accessibleStyle} htmlColor="#4A90E2" />
      </span>}
    </div>
  </div>
);

const locationDetails = (
  {ride, onLocationsEdit, timezone} // eslint-disable-line react/prop-types
) => (
  <div className={styles.sectionItem}>
    {onLocationsEdit &&
      <div className={styles.edit} data-id="edit-pudo" onClick={onLocationsEdit}>Edit</div>}
    <div className={styles.heading}>Planned Ride</div>
    <div data-id="pickup-details">
      <MarkerAIcon style={iconStyle} />
      {ride.status === rideStatus.COMPLETE &&
        <div className={styles.pudoTime}>
          {moment(ridePickupEvent(ride).timestamp).tz(timezone).format('h:mma')}
        </div>
      }
      {ride.pickupName || ride.pickup.address}
    </div>
    <div data-id="dropoff-details">
      <MarkerBIcon style={iconStyle} />
      {ride.status === rideStatus.COMPLETE &&
        <div className={styles.pudoTime}>
          {moment(rideDropoffEvent(ride).timestamp).tz(timezone).format('h:mma')}
        </div>
      }
      {ride.dropoffName || ride.dropoff.address}
    </div>
  </div>
);

const priceDetails = ({ride}) => { // eslint-disable-line react/prop-types
  if (!ride.fare) return null;
  const {cost} = ride.fare;
  return (
    <div className={styles.price}>
      <RidePayment
        active={ride.isActive}
        amount={cost.value}
        currencyCode={cost.currency_code}
        deeplinkService={ride.serviceTokenTransitUseDeeplink}
        method={ride.fare.stripe_charge_id ? 'stripe' : 'vehicle'}
        paid={ride.fare.paid}
      />
    </div>
  );
};

const statusDetails = ({ride}) => { // eslint-disable-line react/prop-types
  if (ride.status === rideStatus.COMPLETE) {
    return (
      <div className={styles.bottomSection}>
        <div className={styles.sectionItem}>
          <div className={styles.complete} data-id="status-details">COMPLETE</div>
        </div>
      </div>
    );
  } else if (ride.status === rideStatus.NO_SHOW) {
    return (
      <div className={styles.bottomSection}>
        <div className={styles.sectionItem}>
          <div className={styles.noShow} data-id="status-details">NO SHOW</div>
        </div>
      </div>
    );
  } else if (ride.status === rideStatus.CANCELED) {
    return (
      <div className={styles.bottomSection}>
        <div className={styles.sectionItem}>
          <div className={styles.canceled} data-id="status-details">CANCELED</div>
          <div className={styles.terminalReason} data-id="terminal-reason">
            {ride.terminalReason}
          </div>
        </div>
      </div>
    );
  } else if (ride.status === rideStatus.DENIED) {
    return (
      <div className={styles.bottomSection}>
        <div className={styles.sectionItem}>
          <div className={styles.canceled} data-id="status-details">DENIED</div>
          <div className={styles.terminalReason} data-id="terminal-reason">
            {ride.terminalReason}
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const RideDetailsInput = ({
  agency,
  load,
  ride,
  onLocationsEdit,
  onPassengerDetailsEdit,
  onPickupDateTimeEdit,
  thresholds,
  timezone,
}) => {
  const {capacity, wheelchair, bicycle} = ride;
  return (
    <div className={styles.root}>
      <div className={styles.topSection}>
        {locationDetails({ride, onLocationsEdit, timezone})}
        {priceDetails({ride})}
      </div>
      <div className={styles.bottomSection}>
        {timeDetails({agency, load, ride, thresholds, timezone, onPickupDateTimeEdit})}
        {passengerDetails({capacity, wheelchair, bicycle, onPassengerDetailsEdit})}
      </div>
      {statusDetails({ride})}
    </div>
  );
};

RideDetailsInput.propTypes = {
  agency: PropTypes.object,
  load: PropTypes.oneOf(['high', 'medium', 'low', 'unknown']),
  onLocationsEdit: PropTypes.func,
  onPassengerDetailsEdit: PropTypes.func,
  onPickupDateTimeEdit: PropTypes.func,
  ride: PropTypes.shape({
    pickup: PropTypes.object,
    dropoff: PropTypes.object,
    capacity: PropTypes.number,
    wheelchair: PropTypes.bool,
    bicycle: PropTypes.bool,
  }).isRequired,
  thresholds: PropTypes.shape({
    medium: PropTypes.number.isRequired,
    high: PropTypes.number.isRequired,
  }).isRequired,
  timezone: PropTypes.string,
};

export default RideDetailsInput;



// WEBPACK FOOTER //
// ./src/ondemand/components/RideDetailsInput/RideDetailsInput.js